import { HttpClient, HttpErrorResponse, HttpEventType } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';
import { IBaseResponse } from 'src/app/model/common.model';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-video-recorder',
  templateUrl: './video-recorder.component.html',
  styleUrls: ['./video-recorder.component.scss']
})
export class VideoRecorderComponent {
  @Input() vehicleId: string;
  @Input() phase: string;
  @Input() parentPartId: string;
  @Output() onUploadSuccess: EventEmitter<string> = new EventEmitter<string>();

  // Recording state flags
  isRecording = false;
  isPaused = false;
  videoRecorded = false;
  isLiveStreaming = false;

  // Camera toggle properties
  currentFacingMode: 'user' | 'environment' = 'environment';
  isMobileDevice = false;

  // Video recording properties
  videoBlob: Blob | null = null;
  videoURL: SafeUrl | null = null;
  liveVideoURL: SafeUrl | null = null;
  mediaRecorder: MediaRecorder | null = null;
  videoChunks: Blob[] = [];
  stream: MediaStream | null = null;
  uploadPercentage: number | null = null;
  isUploading: boolean = false;

  static readonly VIDEO_RECORDING_SHOULD_START_FROM = 90;

  // Timer related properties
  recordingTime = VideoRecorderComponent.VIDEO_RECORDING_SHOULD_START_FROM;
  timerInterval: ReturnType<typeof setInterval> | null = null;

  @ViewChild('videoPlayer', { static: false }) videoPlayer!: ElementRef<HTMLVideoElement>;
  @ViewChild('liveVideoPlayer', { static: false }) liveVideoPlayer!: ElementRef<HTMLVideoElement>;

  constructor(
    private httpClient: HttpClient,
    private sanitizer: DomSanitizer,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef
  ) {
    this.detectMobileDevice();
  }

  detectMobileDevice() {
    this.isMobileDevice =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      window.innerWidth <= 768;
  }

  formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  startTimer() {
    this.timerInterval = setInterval(() => {
      const updatedTimer = this.recordingTime - 1;

      if (updatedTimer === 0) {
        this.stopRecording();
      }
      this.recordingTime = updatedTimer;
    }, 1000);
  }

  stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  async toggleCamera() {
    if (!this.isLiveStreaming || this.isRecording) {
      return;
    }

    try {
      // Switch facing mode
      this.currentFacingMode = this.currentFacingMode === 'user' ? 'environment' : 'user';

      // Stop current stream
      this.stopLiveStream();

      // Start new stream with updated facing mode
      await this.startLiveStream();

      this.toastr.success(
        `Switched to ${this.currentFacingMode === 'user' ? 'front' : 'back'} camera`
      );
    } catch (error) {
      console.error('Error toggling camera:', error);
      this.toastr.error('Failed to switch camera');
      // Revert facing mode if failed
      this.currentFacingMode = this.currentFacingMode === 'user' ? 'environment' : 'user';
    }
  }

  async startLiveStream() {
    try {
      // Stop any existing stream
      this.stopLiveStream();

      // Get new video stream with both audio and video
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: this.currentFacingMode
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100
        }
      });
      this.isLiveStreaming = true;

      if (this.liveVideoPlayer && this.liveVideoPlayer.nativeElement) {
        this.liveVideoPlayer.nativeElement.srcObject = this.stream;
        this.liveVideoPlayer.nativeElement.autoplay = true;
        this.liveVideoPlayer.nativeElement.muted = true;
        this.liveVideoPlayer.nativeElement.playsInline = true;

        try {
          this.liveVideoPlayer.nativeElement.style.height = '400px';
          await this.liveVideoPlayer.nativeElement.play();
        } catch (playError) {
          console.error('Error playing video:', playError);
          // this.toastr.error('Error playing video');
        }
      }

      this.isLiveStreaming = true;
    } catch (error) {
      this.isLiveStreaming = false;
      console.error('Error accessing camera/microphone:', error);
      this.toastr.error(error, 'Error accessing camera/microphone');
    }
  }

  stopLiveStream() {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());

      if (this.liveVideoPlayer && this.liveVideoPlayer.nativeElement) {
        this.liveVideoPlayer.nativeElement.srcObject = null;
        this.liveVideoPlayer.nativeElement.style.height = '0';
      }

      this.stream = null;
      this.isLiveStreaming = false;
    }
  }

  async startRecording() {
    try {
      // If not already streaming, start the live stream
      if (!this.isLiveStreaming) {
        await this.startLiveStream();
      }

      // Prepare media recorder
      if (!this.stream) {
        throw new Error('No media stream available');
      }

      // Use different MIME types based on browser support
      let mimeType = 'video/webm';
      if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9')) {
        mimeType = 'video/webm;codecs=vp9';
      } else if (MediaRecorder.isTypeSupported('video/webm;codecs=vp8')) {
        mimeType = 'video/webm;codecs=vp8';
      } else if (MediaRecorder.isTypeSupported('video/mp4')) {
        mimeType = 'video/mp4';
      }

      this.mediaRecorder = new MediaRecorder(this.stream, {
        mimeType: mimeType,
        audioBitsPerSecond: 128000,
        videoBitsPerSecond: 2500000
      });
      this.videoChunks = [];

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.videoChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        // Ensure we have recorded video chunks
        if (this.videoChunks.length > 0) {
          this.videoBlob = new Blob(this.videoChunks, { type: mimeType });

          // Create a safe URL for the video
          const unsafeUrl = URL.createObjectURL(this.videoBlob);
          this.videoURL = this.sanitizer.bypassSecurityTrustUrl(unsafeUrl);

          this.videoRecorded = true;
          this.stopTimer();
        }

        this.isRecording = false;
        this.isPaused = false;
      };

      // Reset recording time when starting a new recording
      this.recordingTime = VideoRecorderComponent.VIDEO_RECORDING_SHOULD_START_FROM;
      this.mediaRecorder.start();
      this.isRecording = true;
      this.isPaused = false;
      this.startTimer();
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Failed to start recording. Please check your camera and microphone.');
      this.stopLiveStream();
    }
  }

  pauseRecording() {
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.pause();
      this.isPaused = true;
      this.stopTimer();
    }
  }

  resumeRecording() {
    if (this.mediaRecorder && this.mediaRecorder.state === 'paused') {
      this.mediaRecorder.resume();
      this.isPaused = false;
      this.startTimer();
    }
  }

  stopRecording() {
    if (
      this.mediaRecorder &&
      (this.mediaRecorder.state === 'recording' || this.mediaRecorder.state === 'paused')
    ) {
      this.mediaRecorder.stop();
      this.isRecording = false;
      this.stopTimer();

      this.liveVideoPlayer.nativeElement.style.height = '0';
      this.videoRecorded = true;
      this.uploadPercentage = null;

      // Optional: keep live stream active
      // this.stopLiveStream();
    }

    this.forceRender();
  }

  deleteRecording() {
    this.liveVideoPlayer.nativeElement.style.height = '400px';
    this.videoRecorded = false;

    if (this.videoURL) {
      // Revoke the object URL to free up memory
      const url = this.videoURL as string;
      URL.revokeObjectURL(url);
    }
    this.videoURL = null;
    this.videoBlob = null;
    this.videoChunks = [];
    this.recordingTime = 0;

    // Reset the video player if it exists
    if (this.videoPlayer) {
      this.videoPlayer.nativeElement.src = '';
    }
  }

  uploadRecording() {
    if (!this.videoBlob) {
      return;
    }

    const formData = new FormData();
    formData.append('video', this.videoBlob);
    formData.append('vehicle_id', this.vehicleId);
    formData.append('phase', this.phase.toLocaleLowerCase());
    formData.append('parent_part_id', this.parentPartId);
    this.uploadPercentage = 0;
    this.isUploading = true;

    this.httpClient
      .post(`${environment.api}/upload/vehicle-audio-video`, formData, {
        reportProgress: true,
        observe: 'events'
      })
      .subscribe({
        next: (event) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            this.uploadPercentage = Math.round((event.loaded / event.total) * 100);
            this.cdr.detectChanges();
          } else if (event.type === HttpEventType.Response) {
            this.toastr.success('Video uploaded');
            this.videoRecorded = false;
            this.isUploading = false;
            this.deleteRecording();
            if (event.body?.['success']) {
              this.uploadPercentage = null;
              this.onUploadSuccess.emit(event.body?.['url']);
              this.isUploading = false;
            }
          }
        },
        error: (error: HttpErrorResponse) => {
          console.log(error, 'error vehicle-audio-video');
          const res = error?.error as IBaseResponse;
          if (res && res?.errors?.video?.[0]) this.toastr.error(res?.errors?.video?.[0]);
          else this.toastr.error('Upload failed');

          this.isUploading = false;
        }
      });
  }

  ngOnDestroy() {
    // Clean up any active media streams or created object URLs
    this.stopLiveStream();

    if (this.videoURL) {
      const url = this.videoURL as string;
      URL.revokeObjectURL(url);
    }

    this.stopTimer();
  }

  onFileReceived(file: File) {
    this.liveVideoPlayer.nativeElement.style.height = '0';
    this.videoBlob = file;

    const unsafeUrl = URL.createObjectURL(this.videoBlob);
    this.videoURL = this.sanitizer.bypassSecurityTrustUrl(unsafeUrl);

    this.videoRecorded = true;
  }

  forceRender() {
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 1000);
  }
}
